
import Hammer                 from 'hammerjs';
import Konva                  from 'konva';
import { isNil }              from 'lodash';
import * as THREE             from 'three';
import {
  EquirectangularReflectionMapping,
  GridHelper,
  Material,
  MathUtils,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Object3D,
  PerspectiveCamera,
  PointLight,
  Raycaster,
  RepeatWrapping,
  Scene,
  Shape,
  ShapeGeometry,
  TextureLoader,
  Vector2,
  Vector3
}                             from 'three';
import { RGBELoader }         from 'three/examples/jsm/loaders/RGBELoader';
import { degToRad, radToDeg } from 'three/src/math/MathUtils';
import { Vue }                from 'vue-class-component';


// eslint-disable
enum InteractionMode {
  None          = 'none',
  Adjust        = 'adjust',
  Draw          = 'draw',
  TextureMove   = 'texture-move',
  TextureRotate = 'texture-rotate',
  TextureZoom   = 'texture-zoom',
}


export default class Render extends Vue {

  $refs!: any;
  requestExport: any;

  orbitControls: any = null;

  private _interactionMode: InteractionMode = InteractionMode.None;

  public get interactionMode(): InteractionMode {
    return this._interactionMode;
  }

  public set interactionMode(value: InteractionMode) {
    this._interactionMode = value;
    this.updateRenderClasses();
    this.disableMobileControlsOverlay();
  }

  showMobileControlsOverlay = false;
  showButtons               = true;


  renderWidth: number;
  renderHeight: number;

  hammer: any; // TODO type

  textureOffset: Vector2 = new Vector2(0, 0);
  textureRotation        = 50;
  textureScale           = 4;

  readonly _globalTextureScaleModifier = .75;

  private _raycaster: Raycaster                    = new Raycaster();
  private _raycasterMouseNormalizedCoords          = new Vector2();
  private _mouseScreenPosition!: Vector2;
  private _grid!: GridHelper;
  private _tileMeshes?: Mesh[]                     = [];
  private _tileShapeMaterial: MeshStandardMaterial = new MeshStandardMaterial({
    roughness: 0.8
  });

  private _kvStage!: Konva.Stage;
  private _kvLineLayer   = new Konva.Layer();
  private _kvCircleLayer = new Konva.Layer();

  private _shapes: Vector3[][] = [];
  private _shapeEditingIndex!: number;

  private _tileTextureLoading  = true;
  private _sceneTextureLoading = true;

  private _takeScreenshot = false;

  readonly debug = true;

  readonly cameraX = 0;
  readonly cameraY = 2;
  readonly cameraZ = 5;

  readonly backgroundDistance    = 50;
  readonly backgroundPlaneWidth  = 93.5;
  readonly backgroundPlaneHeight = 48;

  readonly intersectionPlaneWidth    = 40;
  readonly intersectionPlaneHeight   = 30;
  readonly intersectionPlaneRotation = { x: MathUtils.degToRad(-90), y: 0, z: 0 };


  get isNoneMode() { return this.interactionMode === InteractionMode.None; }

  get isAdjustMode() { return this.interactionMode === InteractionMode.Adjust; }

  get isDrawMode() { return this.interactionMode === InteractionMode.Draw; }

  get isTextureMoveMode() { return this.interactionMode === InteractionMode.TextureMove; }

  get isTextureRotateMode() { return this.interactionMode === InteractionMode.TextureRotate; }

  get isTextureZoomMode() { return this.interactionMode === InteractionMode.TextureZoom; }

  private activeTile = null;

  mounted() {
    const renderer                  = this.$refs.renderer;
    const scene: Scene              = this.$refs.scene.scene;
    const camera: PerspectiveCamera = this.$refs.camera.camera;

    (window as any).scene = scene;
    (window as any).THREE = THREE;

    (window as any)._render = this;

    this.$store.watch((state) => {return this.$store.state.takeScreenshot;}, x => {
      this._takeScreenshot = x;
    });

    renderer.addListener('afterrender', (x) => {
      if (this._takeScreenshot) {
        this.handleExport(x.renderer.canvas.toDataURL('image/jpeg'));
        this._takeScreenshot = false;
        this.$store.commit('set_takeScreenshot', false);
        this.$router.push(this.$router.currentRoute.value.meta.next);
      }
    });

    // Add listeners
    window.addEventListener('mousemove', this.onMouseMove, false);
    window.addEventListener('mouseup', this.onMouseClick, false);

    // Add camera to scene
    scene.add(camera);

    // Add a light
    if (this.$store.state.previewImageUrl.includes('d2.jpg')) {
      const light = new PointLight(0xffffff, 4.1, 500);
      //light.position.set(0,4,5);
      light.position.set(0, 8, 20);
      scene.add(light);
    } else {
      const light = new PointLight(0xffffff, 2.3, 500);
      //light.position.set(0,4,5);
      light.position.set(0, 8, 12);
      scene.add(light);
    }

    // Set up orbit controls
    this.orbitControls = {
      enableDamping  : false,
      dampingFactor  : 0.05,
      enableRotate   : false,
      enablePan      : false,
      enableZoom     : false,
      minAzimuthAngle: 0,
      maxAzimuthAngle: 0,
      minPolarAngle  : Math.PI / 4,
      maxPolarAngle  : Math.PI / 1.75,
      target         : new Vector3(0, this.cameraY, 0),
      rotateSpeed    : .25,
      panSpeed       : 1
    };
    this.applyOrbitControls();

    // Set up HDRI
    new RGBELoader().load('./immenstadter_horn_1k.hdr', (texture) => {
      texture.mapping = EquirectangularReflectionMapping;

      // scene.background  = texture;
      // scene.background  = new Color(0x000000);
      // scene.environment = texture;
    });

    // const lightHelper = new AxesHelper(5);
    // light.add(lightHelper);

    // Add grid helper
    this._grid                                    = new GridHelper(this.intersectionPlaneHeight, this.intersectionPlaneHeight, 0xffffff, 0xffffff);
    this._grid.position.z                         = -0.001; // Prevent visual artifacts
    (this._grid.material as Material).transparent = true;
    (this._grid.material as Material).opacity     = 0;
    this.$refs.scene.add(this._grid);

    // Attach background layer to camera
    const backgroundPlane: Mesh = this.$refs.backgroundPlane.mesh;
    camera.add(backgroundPlane);
    backgroundPlane.position.set(0, 0, -this.backgroundDistance);

    // Adjust intersection plane material
    const intersectionPlane: Mesh                                 = this.$refs.intersectionPlane.mesh;
    (intersectionPlane.material as MeshBasicMaterial).transparent = true;
    (intersectionPlane.material as MeshBasicMaterial).opacity     = 0;
    (intersectionPlane.material as MeshBasicMaterial).color.setHex(0x000000);
    (intersectionPlane.material as MeshBasicMaterial).needsUpdate = true;

    const selectedTiles = this.$store.state?.selectedTiles || [];
    const activeTile    = selectedTiles.find(x => x.active);

    // Preloaded data
    if (!this.$store.state.previewImageUrl) {
      this.$store.state.previewImageUrl = 'img/house-presets/d1.jpg';
    }

    this.setSceneBackground(this.$store.state.previewImageUrl);
    this.setTileTexture(activeTile?.tileRender || 'https://salesqueze-production-fs.s3.amazonaws.com/oblak/assets/mesh.png');

    this.textureOffset   = activeTile?.offset ?? this.textureOffset;
    this.textureScale    = activeTile?.scale ?? activeTile?.attributes?.scale ?? this.textureScale;
    this.textureRotation = activeTile?.rotation ?? activeTile?.attributes?.rotation ?? this.textureRotation;

    this.updateTileMaterial();

    this.$store.watch((state) => {return this.$store.state.selectedTiles;}, x => {
          if (x.length > 0) {
            const activeTile = x.find(y => y.active);

            if (this._tileShapeMaterial.map?.image?.currentSrc !== activeTile?.tileRender) {
              this.setTileTexture(activeTile?.tileRender);
            }

            this.textureOffset   = activeTile?.offset ?? this.textureOffset;
            this.textureScale    = activeTile?.scale ?? activeTile?.attributes?.scale ?? this.textureScale;
            this.textureRotation = activeTile?.rotation ?? activeTile?.attributes?.rotation ?? this.textureRotation;

            this.updateTileMaterial();
          }
        },
        { deep: true });

    // Konva
    this._kvStage = new Konva.Stage({
      container: this.$refs.konvaOverlayContainer,
      // first just set set as is
      width : 100,
      height: 100
    });

    this._kvStage.add(this._kvLineLayer);
    this._kvStage.add(this._kvCircleLayer);

    this.fitRenderIntoContainer();
    this.fitKonvaStageIntoParentContainer();

    const img                                              = this.$store.state.previewImageUrl;
    this.$refs.renderer.three.cameraCtrl.object.rotation.x = img?.includes('d3.jpg') ? 0.05864306286701118 : img?.includes('d2.jpg') ? 0.017054360119486356 : 0.031415926535899925;
    this.$refs.renderer.three.cameraCtrl.object.position.set(0,
        img?.includes('d3.jpg') ? 2.293047281555487 : img?.includes('d2.jpg') ? 1.9998638808167684 : 1.8429462046093505,
        img?.includes('d3.jpg') ? 4.991404941574291 : img?.includes('d2.jpg') ? 0.056867998655537766 : 4.997532801828595
    );

    const pointsD1 = [
      new Vector3(-5.428338065183027, 5.524970874136522e-16, -2.4682256769994084),
      new Vector3(-6.219515221557417, 5.77288263409591e-16, -3.599875207976795),
      new Vector3(-7.016366995047425, 7.564774604296222e-16, -4.406871608905026),
      new Vector3(-7.278023836249155, 8.812244711120721e-16, -4.9686821997300905),
      new Vector3(1.3570953450181782, 3.0893370629609314e-15, -14.913137245572713),
      new Vector3(13.552576771403272, 1.5352002638389493e-15, -8.013927336164179),
      new Vector3(4.9848097747373226, -3.632850177762334e-16, 1.0060902706863287),
      new Vector3(-2.3032800820422346, -3.687969842250159e-16, 0.688139607311443),
      new Vector3(-5.428338065183027, 5.524970874136522e-16, -2.4682256769994084)
    ];

    const pointsD2 = [
      new Vector3(-7.955256688497297, 1.6488319559278007e-15, -7.425678982313009),
      new Vector3(-0.22412715226348912, 3.323570654301671e-15, -14.968031560252522),
      new Vector3(10.223453562238862, 2.3366827093980895e-15, -10.523483379328317),
      new Vector3(4.364123063415413, 1.1806895904294692e-15, -4.017353199498381),
      new Vector3(-4.353111420641439, 1.1780608318347026e-15, -4.005514323270543),
      new Vector3(-7.955256688497297, 1.6488319559278007e-15, -7.425678982313009)
    ];

    const pointsD3 = [
      new Vector3(-2.4485187680078573, -1.6276142329328824e-16, 0.7380122852939445),
      new Vector3(-5.073767656687658, 2.5356428755505466e-15, -9.419520309474095),
      new Vector3(2.57041585735302169, 2.1695582339743026e-15, -9.77082165408526),
      new Vector3(3.3157537049115016, -3.222826833382511e-15, -14.514321725901112),
      new Vector3(14.945593188679764, 3.3174115991752295e-15, -14.940293641880125),
      new Vector3(12.441783962252123, 1.8131545961510386e-15, -8.16572236359092),
      new Vector3(4.553659859701003, -1.534094331444437e-16, 0.8008946659444357),
      new Vector3(-2.4485187680078573, -1.6276142329328824e-16, 0.7380122852939445)
    ];

    const points = img?.includes('d3.jpg') ? pointsD3 : img?.includes('d2.jpg') ? pointsD2 : pointsD1;

    this.addStaticShapePoints(points);

    setTimeout(() => {
      this.setUpGestures();
      this.updateRenderClasses();

      window.dispatchEvent(new Event('resize'));
      window.addEventListener('resize', this.onResize, false);

      if (this.isAutomatedGuideFlow()) {
        this.enableAdjustMode();
      }
    }, 0);
  }

  unmounted() {
    window.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('mouseup', this.onMouseClick);

    window.removeEventListener('resize', this.onResize);

    this.hammer.destroy();
  }

  handleExport(image: string) {
    this.$store.commit('setScreenshot', image);
  }

  isDesktop() {
    return window.matchMedia('(min-width: 768px)').matches;
  }

  isAutomatedGuideFlow() {
    if (this.$store.state.previewImageUrl?.includes('house-presets')) {
      this.showButtons = false;
      return false;
    }
    this.showButtons                = !this.$store.state.automatedGuideFlow;
    this.$refs.loader.style.display = 'none';
    return !this.$store.state.automatedGuideFlow;
  }

  setSceneBackground(url: string) {
    this._sceneTextureLoading = true;
    this.updateLoaderOverlayState();

    const backgroundPlane: Mesh = this.$refs.backgroundPlane.mesh;

    new TextureLoader().load(url, (texture) => {
      const mat: MeshBasicMaterial = backgroundPlane.material as any;

      // CSS cover-style texture resize
      texture.matrixAutoUpdate = false;

      const aspect      = this.backgroundPlaneWidth / this.backgroundPlaneHeight;
      const imageAspect = texture.image.width / texture.image.height;

      if (aspect < imageAspect) {
        texture.matrix.setUvTransform(0, 0, aspect / imageAspect, 1, 0, 0.5, 0.5);
      } else {
        texture.matrix.setUvTransform(0, 0, 1, imageAspect / aspect, 0, 0.5, 0.5);
      }

      mat.opacity     = 1;
      mat.transparent = true;

      mat.map         = texture;
      mat.needsUpdate = true;

      this._sceneTextureLoading = false;
      this.updateLoaderOverlayState();
    });
  }

  changeImage() {
    this.$router.push({ path: '/start' });
  }

  enableAdjustMode() {
    this.removeAllShapePoints();
    this.removeMeshes();

    const intersectionPlane: Mesh                                 = this.$refs.intersectionPlane.mesh;
    (intersectionPlane.material as MeshBasicMaterial).opacity     = .3;
    (intersectionPlane.material as MeshBasicMaterial).needsUpdate = true;

    (this._grid.material as Material).opacity     = 1;
    (this._grid.material as Material).needsUpdate = true;

    this.orbitControls.enableRotate = true;
    this.applyOrbitControls();

    this.interactionMode = InteractionMode.Adjust;
  }

  disableAdjustMode() {
    const intersectionPlane: Mesh                                 = this.$refs.intersectionPlane.mesh;
    (intersectionPlane.material as MeshBasicMaterial).opacity     = 0;
    (intersectionPlane.material as MeshBasicMaterial).needsUpdate = true;

    (this._grid.material as Material).opacity     = 0;
    (this._grid.material as Material).needsUpdate = true;

    this.orbitControls.enableRotate = false;
    this.applyOrbitControls();

    this.interactionMode = InteractionMode.None;

    const x = this.$refs.renderer.three.cameraCtrl.object.rotation.x;
    const y = this.$refs.renderer.three.cameraCtrl.object.rotation.y;
    const z = this.$refs.renderer.three.cameraCtrl.object.rotation.z;

    const pos = this.$refs.renderer.three.cameraCtrl.object.position;


    if (this.isAutomatedGuideFlow()) {
      this.enableDrawMode();
    }
  }

  enableDrawMode() {
    this.removeMeshes();
    this.interactionMode = InteractionMode.Draw;
  }

  disableDrawMode() {
    this.removeMeshes();
    this.updateDrawingGraphics();
    this.interactionMode = InteractionMode.None;
  }

  enableTextureMoveMode() {
    this.interactionMode = InteractionMode.TextureMove;
  }

  disableTextureMoveMode() {
    this.interactionMode = InteractionMode.None;
    this.storeTileTransform();
  }

  enableTextureRotateMode() {
    this.interactionMode = InteractionMode.TextureRotate;
  }

  disableTextureRotateMode() {
    this.interactionMode = InteractionMode.None;
    this.storeTileTransform();
  }

  enableTextureZoomMode() {
    this.interactionMode = InteractionMode.TextureZoom;
  }

  disableTextureZoomMode() {
    this.interactionMode = InteractionMode.None;
    this.storeTileTransform();
  }

  disableDrawModeAndCreateMeshes() {
    this.removeMeshes();
    this.interactionMode = InteractionMode.None;
    this.updateDrawingGraphics();
    this.createMeshes();
  }

  incrementMeshCreatedCounter(){
    if((this.$store.state.selectedTiles || []).length === 0){
      this.$store.commit('incrementMeshCreated')
    }
  }

  storeTileTransform() {
    const selectedTiles = this.$store.state.selectedTiles;
    if (!selectedTiles?.length) {
      return;
    }

    const index = selectedTiles.findIndex(x => x.active);
    if (index < 0) {
      return;
    }

    selectedTiles[index].scale    = this.textureScale;
    selectedTiles[index].rotation = this.textureRotation;
    selectedTiles[index].offset   = this.textureOffset.clone();
  }

  createMeshes() {
    this.removeMeshes();

    const scene: Scene = this.$refs.scene.scene;

    for (const shapePoints of this._shapes) {
      const shape = new Shape();
      shape.moveTo(shapePoints[0].x, -shapePoints[0].z);
      for (let i = 1; i < shapePoints.length; i++) {
        shape.lineTo(shapePoints[i].x, -shapePoints[i].z);
      }
      shape.lineTo(shapePoints[0].x, -shapePoints[0].z);

      const geometry = new ShapeGeometry(shape);
      geometry.rotateX(MathUtils.degToRad(-90));

      const mesh      = new Mesh(geometry, this._tileShapeMaterial);
      mesh.position.z = 0.001;
      this._tileMeshes!.push(mesh);
      scene.add(mesh);
    }
  }

  setTileTexture(texture: string) {
    this._tileTextureLoading = true;
    this.updateLoaderOverlayState();

    const textureLoader = new TextureLoader();

    const map = textureLoader.load(texture, () => {
      this._tileTextureLoading = false;
      this.updateLoaderOverlayState();
    });
    // map.wrapS      = MirroredRepeatWrapping;
    // map.wrapT      = MirroredRepeatWrapping;
    map.wrapS      = RepeatWrapping;
    map.wrapT      = RepeatWrapping;
    map.anisotropy = this.$refs.renderer.renderer.getMaxAnisotropy();

    this._tileShapeMaterial.map = map;

    this.updateTileMaterial();
  }

  updateTileMaterial() {
    this.setTileOffset(this.textureOffset);
    this.setTileScale(this.textureScale);
    this.setTileRotation(this.textureRotation);

    this._tileShapeMaterial.needsUpdate = true;
  }

  setTileRotation(rotation: number) {
    // console.log('Setting rotation to', rotation, 'deg');
    this._tileShapeMaterial.map!.rotation = degToRad(rotation);
    this._tileShapeMaterial.needsUpdate   = true;
  }

  setTileScale(scale: number) {
    // console.log('Setting scale to', scale);
    this._tileShapeMaterial.map!.repeat = new Vector2(1 / scale * this._globalTextureScaleModifier, 1 / scale * this._globalTextureScaleModifier);
    this._tileShapeMaterial.needsUpdate = true;
  }

  setTileOffset(offset: Vector2) {
    // console.log('Setting offset to', offset);
    this._tileShapeMaterial.map!.offset = offset.clone();
    this._tileShapeMaterial.needsUpdate = true;
  }

  removeMeshes() {
    if (this._tileMeshes?.length) {
      this._tileMeshes.forEach(mesh => {
        mesh.parent?.remove(mesh);
      });
      this._tileMeshes = [];
    }
  }

  disableMobileControlsOverlay() {
    this.showMobileControlsOverlay = false;
  }

  toggleMobileControlsOverlay() {
    this.showMobileControlsOverlay = !this.showMobileControlsOverlay;
  }

  private updateLoaderOverlayState() {
    this.$refs.loader.style.display = 'none';
    if (this._tileTextureLoading || this._sceneTextureLoading) {
      this.$refs.loader.style.display = ''; // Clear display style override
    }
  }

  private fitKonvaStageIntoParentContainer() {
    const container = this.$refs!.konvaOverlayContainer;

    // now we need to fit stage into parent container
    const containerWidth  = container.offsetWidth;
    const containerHeight = container.offsetHeight;

    this._kvStage.width(containerWidth);
    this._kvStage.height(containerHeight);
  }

  private fitRenderIntoContainer() {
    const container       = this.$refs.renderContainer;
    const containerWidth  = container.offsetWidth;
    const containerHeight = container.offsetHeight;

    const content = this.$refs.renderContent;
    if (!content) return;

    const ratio = 16 / 8;

    if (this.isDesktop()) {
      // Desktop layout
      if (containerWidth > containerHeight * ratio) {
        this.renderWidth  = containerHeight * ratio;
        this.renderHeight = containerHeight;
      } else {
        this.renderWidth  = containerWidth;
        this.renderHeight = containerWidth / ratio;
      }
    } else {
      // Mobile layout
      this.renderWidth  = containerWidth;
      this.renderHeight = containerWidth / ratio;
    }

    this.renderWidth  = Math.floor(this.renderWidth);
    this.renderHeight = Math.floor(this.renderHeight);

    content.style.width  = `${ this.renderWidth }px`;
    content.style.height = `${ this.renderHeight }px`;


    this.$refs.camera.camera.aspect = this.renderWidth / this.renderHeight;
    this.$refs.camera.camera.updateProjectionMatrix();

    this.$refs.renderer.renderer.setSize(this.renderWidth, this.renderHeight);
  }

  private setUpGestures() {
    const canvas: HTMLCanvasElement = document.querySelector('.render canvas');

    this.hammer = new Hammer(canvas);


    let snapshotTextureOffset: Vector2;
    let snapshotTextureRotation: number;
    let snapshotTextureScale: number;

    const origin     = new Vector2(0, 0);
    const tempVector = new Vector2(0, 0);

    // Move, rotate, zoom
    this.hammer.on('panstart', (event: any) => {
      snapshotTextureOffset   = this.textureOffset.clone().rotateAround(origin, degToRad(this.textureRotation));
      snapshotTextureRotation = degToRad(this.textureRotation);
      snapshotTextureScale    = this.textureScale;
    });

    this.hammer.on('pan', (event: any) => {
      if (this.isNoneMode) { return; }
      event.preventDefault();
      switch (this.interactionMode) {
        case InteractionMode.TextureMove: {
          // Calculate new position
          tempVector.set(snapshotTextureOffset.x - event.deltaX * 0.0045, snapshotTextureOffset.y + event.deltaY * 0.0075);
          tempVector.rotateAround(origin, -degToRad(this.textureRotation));

          this.textureOffset.x = tempVector.x;
          this.textureOffset.y = tempVector.y;
          this.updateTileMaterial();
          break;
        }
        case InteractionMode.TextureRotate: {
          this.textureRotation = radToDeg(snapshotTextureRotation - event.deltaX * 0.008);
          this.updateTileMaterial();
          break;
        }
        case InteractionMode.TextureZoom: {
          this.textureScale = Math.max(2, Math.min(snapshotTextureScale - event.deltaY * 0.01, 6));
          this.updateTileMaterial();
          break;
        }
      }
    });


    this.hammer.add(new Hammer.Pan({ event: 'doublepan', pointers: 2 }));

    let snapshotScrollLeft: number;
    this.hammer.on('doublepanstart', (event: any) => {
      snapshotScrollLeft = this.$refs.renderContainer.scrollLeft;
      event.preventDefault();
    });

    this.hammer.on('doublepan', (event: any) => {
      this.$refs.renderContainer.scrollLeft = Math.max(0, Math.min(snapshotScrollLeft - event.deltaX, this.$refs.renderContainer.scrollWidth));
      event.preventDefault();
    });
  }

  private async updateRenderClasses() {
    const canvas: HTMLCanvasElement = document.querySelector('.render canvas');
    canvas.style.touchAction        = 'auto';

    if (this.interactionMode !== InteractionMode.None) {
      canvas.style.touchAction = 'none';
    }
  }

  private onMouseMove(event: MouseEvent) {
    const rect                             = this.$refs.canvasContainerElement.getBoundingClientRect();
    this._raycasterMouseNormalizedCoords.x = ((event.clientX - rect.left) / (rect.right - rect.left)) * 2 - 1;
    this._raycasterMouseNormalizedCoords.y = -((event.clientY - rect.top) / (rect.bottom - rect.top)) * 2 + 1;

    if (this.isDrawMode) {
      this._mouseScreenPosition = new Vector2(event.clientX - rect.left, event.clientY - rect.top);
      this.updateDrawingGraphics();
    }
  }

  private onMouseClick(event: MouseEvent) {
    if ((event.target as HTMLElement).nodeName !== 'CANVAS') {
      return;
    }

    if (this.isDrawMode) {
      if (event.button === 0) {
        // Add shape point
        this._raycaster.setFromCamera(this._raycasterMouseNormalizedCoords, this.$refs.camera.camera);
        const intersectionPlane: Object3D = this.$refs.intersectionPlane.mesh;
        const intersects                  = this._raycaster.intersectObjects([intersectionPlane]);

        if (intersects[0]) {
          this.addShapePoint(intersects[0].point);
        }
      } else if (event.button === 2) {
        // Remove shape point
        this.removeLastShapePoint();
      }
    }
  }

  private onResize() {
    this.fitRenderIntoContainer();
    this.fitKonvaStageIntoParentContainer();
    this.updateDrawingGraphics();
  }

  private async addStaticShapePoints(points) {
    if (isNil(this._shapeEditingIndex)) {
      this._shapeEditingIndex = this._shapes.length;
    }

    if (!this._shapes[this._shapeEditingIndex]) {
      this._shapes[this._shapeEditingIndex] = [];
    }

    // const firstPointInShape = this._shapes[this._shapeEditingIndex][0];
    // if (firstPointInShape) {
    //   const pointScreenPosition             = this.toScreenPosition(points[0]);
    //   const firstPointInShapeScreenPosition = this.toScreenPosition(firstPointInShape);
    //
    //   if (pointScreenPosition.distanceTo(firstPointInShapeScreenPosition) <= 15) { // px threshold
    //     // Finish shape
    //     this._shapes[this._shapeEditingIndex].push(firstPointInShape);
    //     this._shapeEditingIndex = null;
    //     this.updateDrawingGraphics();
    //     return;
    //   }
    // }

    for (let [i, point] of points.entries()) {

      this._shapes[0].push(point);
    }

    this._shapes[0].push(points[0]);

    this.enableDrawMode();
    this.disableDrawModeAndCreateMeshes();
  }

  private addShapePoint(point: Vector3) {
    if (isNil(this._shapeEditingIndex)) {
      this._shapeEditingIndex = this._shapes.length;
    }

    if (!this._shapes[this._shapeEditingIndex]) {
      this._shapes[this._shapeEditingIndex] = [];
    }

    const firstPointInShape = this._shapes[this._shapeEditingIndex][0];
    if (firstPointInShape) {
      const pointScreenPosition             = this.toScreenPosition(point);
      const firstPointInShapeScreenPosition = this.toScreenPosition(firstPointInShape);

      if (pointScreenPosition.distanceTo(firstPointInShapeScreenPosition) <= 15) { // px threshold
        // Finish shape
        this._shapes[this._shapeEditingIndex].push(firstPointInShape);
        this._shapeEditingIndex = null;
        this.updateDrawingGraphics();
        return;
      }
    }

    this._shapes[this._shapeEditingIndex].push(point);
    this.updateDrawingGraphics();
  }

  private removeLastShapePoint() {
    if (!this._shapes.length) {
      return;
    }

    if (isNil(this._shapeEditingIndex)) {
      this._shapeEditingIndex = this._shapes.length - 1;
    }

    if (this._shapeEditingIndex < 0) { return; }
    if (!this._shapes[this._shapeEditingIndex].length) {
      this._shapeEditingIndex--;
    }

    const points = this._shapes[this._shapeEditingIndex];
    if (!points) { return; }
    points.pop();

    if (!points.length) {
      this._shapeEditingIndex! = null;
      this._shapes.pop();
    }

    this.updateDrawingGraphics();
  }

  private removeAllShapePoints() {
    this._shapes            = [];
    this._shapeEditingIndex = null;

    this.updateDrawingGraphics();
  }

  private applyOrbitControls() {
    const three = this.$refs.renderer.three;

    three.cameraCtrl.enableDamping   = this.orbitControls.enableDamping;
    three.cameraCtrl.dampingFactor   = this.orbitControls.dampingFactor;
    three.cameraCtrl.enableRotate    = this.orbitControls.enableRotate;
    three.cameraCtrl.enablePan       = this.orbitControls.enablePan;
    three.cameraCtrl.enableZoom      = this.orbitControls.enableZoom;
    three.cameraCtrl.minAzimuthAngle = this.orbitControls.minAzimuthAngle;
    three.cameraCtrl.maxAzimuthAngle = this.orbitControls.maxAzimuthAngle;
    three.cameraCtrl.minPolarAngle   = this.orbitControls.minPolarAngle;
    three.cameraCtrl.maxPolarAngle   = this.orbitControls.maxPolarAngle;
    three.cameraCtrl.target          = this.orbitControls.target;
    three.cameraCtrl.rotateSpeed     = this.orbitControls.rotateSpeed;
    three.cameraCtrl.panSpeed        = this.orbitControls.panSpeed;
  }

  private updateDrawingGraphics() {
    this._kvLineLayer.removeChildren();
    this._kvCircleLayer.removeChildren();

    if (!this.isDrawMode) {
      return;
    }

    const isDesktop = this.isDesktop();

    let shapeIndex = 0;
    for (const shape of this._shapes) {
      let pointIndex = 0;
      for (const point of shape) {
        const screenPosition = this.toScreenPosition(point);

        const circle = new Konva.Circle({
          x          : screenPosition.x,
          y          : screenPosition.y,
          radius     : 6,
          fill       : 'white',
          stroke     : '#0c5da8',
          strokeWidth: 1
        });

        this._kvCircleLayer.add(circle);

        if (pointIndex > 0) {
          const previousPoint = this.toScreenPosition(shape[pointIndex - 1]);

          const line = new Konva.Line({
            points     : [previousPoint.x, previousPoint.y, screenPosition.x, screenPosition.y],
            stroke     : '#0c5da8',
            strokeWidth: 3,
            lineCap    : 'round'
          });

          this._kvLineLayer.add(line);
        }
        pointIndex++;
      }

      // Current position
      if (isDesktop) {
        if (!isNil(this._shapeEditingIndex) && this._shapeEditingIndex === shapeIndex && this._mouseScreenPosition) {
          const circle = new Konva.Circle({
            x          : this._mouseScreenPosition.x,
            y          : this._mouseScreenPosition.y,
            radius     : 6,
            fill       : '#0c5da8',
            stroke     : '#0c5da8',
            strokeWidth: 1
          });

          this._kvCircleLayer.add(circle);

          if (pointIndex > 0) {
            const lastPointPosition = this.toScreenPosition(shape[pointIndex - 1]);
            const line              = new Konva.Line({
              points     : [lastPointPosition.x, lastPointPosition.y, this._mouseScreenPosition.x, this._mouseScreenPosition.y],
              stroke     : '#0c5da8',
              strokeWidth: 3,
              lineCap    : 'round'
            });

            this._kvLineLayer.add(line);
          }
        }
      }

      shapeIndex++;
    }

  }

  private toScreenPosition(position: Vector3): Vector2 {
    const vector = new Vector3(position.x, position.y, position.z);
    const canvas = this.$refs.renderer.canvas;

    // map to normalized device coordinate (NDC) space
    vector.project(this.$refs.camera.camera);

    // map to 2D screen space
    vector.x = Math.round((vector.x + 1) * canvas.width / 2);
    vector.y = Math.round((-vector.y + 1) * canvas.height / 2);

    return new Vector2(vector.x, vector.y);
  }

}
